/**
 * Wizard API Service
 *
 * API calls specific to the conversion wizard flow.
 * Integrates with the NIB parser backend from Session 2.3.
 *
 * Session 4.1: Wizard State Machine + Steps 1-2
 */

import apiClient, { handleApiResponse } from '@/services/api/client';
import type { ApiResponse } from '@/types';
import { v4 as uuidv4 } from 'uuid';
import type {
  SourceType,
  WizardEmployee,
  ParseResult,
  GeneratedFile,
} from '../context/types';

// =============================================================================
// BACKEND RESPONSE TYPES
// =============================================================================

interface ParserSource {
  name: string;
  display_name: string;
  description: string;
  confidence: number;
  accepts: string[];
}

interface DetectionResult {
  detected_source: string | null;
  confidence: number;
  available_sources: ParserSource[];
  columns: string[];
  sample_rows: string[][];
}

interface ParsedEmployee {
  employee_ni: string;
  last_name: string;
  first_name: string;
  middle_initial?: string;
  basic_wages: number;
  gratuity_wages: number;
  weeks_worked: number;
  category: number;
  row_number: number;
}

interface BackendParseResult {
  success: boolean;
  source_type: string;
  confidence: number;
  employees: ParsedEmployee[];
  errors: string[];
  warnings: string[];
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Map backend employee to wizard employee format.
 */
function mapEmployee(emp: ParsedEmployee, index: number): WizardEmployee {
  return {
    id: uuidv4(),
    employeeNi: emp.employee_ni || '',
    lastName: emp.last_name || '',
    firstName: emp.first_name || '',
    middleInitial: emp.middle_initial,
    basicWages: emp.basic_wages || 0,
    gratuityWages: emp.gratuity_wages || 0,
    weeksWorked: emp.weeks_worked || 4,
    payIndicator: 'W',
    category: emp.category || 1,
    isRetired: false,
    sourceRow: emp.row_number || index + 1,
    hasNiMatch: emp.employee_ni?.length === 8,
    isNew: false,
  };
}

/**
 * Map source type string to SourceType enum.
 */
function mapSourceType(source: string): SourceType {
  const lower = source.toLowerCase();
  if (lower.includes('quickbooks')) return 'quickbooks';
  if (lower.includes('csv') || lower.includes('generic')) return 'csv';
  return 'csv';
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

export const wizardApi = {
  /**
   * Detect file format and get available parsers.
   *
   * POST /api/v1/nib-parser/detect
   */
  detectFormat: async (file: File): Promise<DetectionResult> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<ApiResponse<DetectionResult>>(
      '/v1/nib-parser/detect',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return handleApiResponse(response);
  },

  /**
   * Parse file with specified parser.
   *
   * POST /api/v1/nib-parser/parse
   */
  parseFile: async (
    file: File,
    sourceType: SourceType
  ): Promise<ParseResult> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('source_type', sourceType);

    try {
      const response = await apiClient.post<ApiResponse<BackendParseResult>>(
        '/v1/nib-parser/parse',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      const data = handleApiResponse(response);

      return {
        success: data.success,
        sourceType: mapSourceType(data.source_type),
        confidence: data.confidence,
        employees: data.employees.map(mapEmployee),
        errors: data.errors || [],
        warnings: data.warnings || [],
      };
    } catch (error) {
      return {
        success: false,
        sourceType,
        confidence: 0,
        employees: [],
        errors: [
          error instanceof Error ? error.message : 'Failed to parse file',
        ],
        warnings: [],
      };
    }
  },

  /**
   * Get available parser sources.
   *
   * GET /api/v1/nib-parser/sources
   */
  getSources: async (): Promise<ParserSource[]> => {
    const response = await apiClient.get<ApiResponse<ParserSource[]>>(
      '/v1/nib-parser/sources'
    );
    return handleApiResponse(response);
  },

  /**
   * Generate C10 file from wizard data.
   *
   * This creates a filing record and generates the file.
   *
   * POST /api/v1/filings
   */
  generateC10: async (params: {
    employerNi: string;
    contributionMonth: number;
    contributionYear: number;
    formatType: 'HOSPITALITY' | 'NON_HOSPITALITY';
    employees: WizardEmployee[];
  }): Promise<GeneratedFile> => {
    // Build the contribution period string (MMYY)
    const period = `${String(params.contributionMonth).padStart(2, '0')}${String(params.contributionYear).slice(-2)}`;

    // Map employees to backend format
    const employeeData = params.employees.map(emp => ({
      employee_ni: emp.employeeNi,
      last_name: emp.lastName,
      first_name: emp.firstName,
      middle_initial: emp.middleInitial || '',
      basic_wages: emp.basicWages,
      gratuity_wages: emp.gratuityWages,
      weeks_worked: emp.weeksWorked,
      category: emp.category,
      pay_indicator: emp.payIndicator,
    }));

    const response = await apiClient.post<
      ApiResponse<{
        id: string;
        file_url: string;
        filename: string;
        total_contributions: number;
      }>
    >('/v1/filings', {
      employer_ni: params.employerNi,
      contribution_period: period,
      format_type: params.formatType,
      employees: employeeData,
    });

    const data = handleApiResponse(response);

    return {
      url: data.file_url,
      filename: data.filename,
      generatedAt: new Date(),
      employeeCount: params.employees.length,
      totalContributions: data.total_contributions,
      filingId: data.id,
    };
  },
};

export default wizardApi;
